import React from "react";

export function TenureCampusDecisions(): React.JSX.Element {
  return (
    <>
      <section
        id="lease-or-own"
        className="tenure-product-section tenure-reveal"
      >
        <p className="tenure-product-eyebrow">The capital decision</p>
        <h2>Lease the campus. Or own it.</h2>
        <p className="tenure-product-lede">
          Evaluate leasing versus owning before committing to either. Compare
          the present value of modelled rent with the upfront capital and
          assumed residual value of ownership.
        </p>
        <div className="tenure-product-output-grid">
          <article>
            <h3>Lease</h3>
            <p>
              See the long-term rent commitment alongside enrolment, fees and
              operating cash flow.
            </p>
          </article>
          <article>
            <h3>Own</h3>
            <p>
              Test the capital required and the asset value assumed at the end
              of the comparison.
            </p>
          </article>
        </div>
        <p className="tenure-product-helper">
          The current ownership comparison is indicative. Financing constraints,
          ongoing ownership costs, lifecycle works and tax need a fuller
          project-specific assessment.
        </p>
      </section>

      <section
        id="opening-date"
        className="tenure-product-section tenure-reveal"
      >
        <p className="tenure-product-eyebrow">Before the first intake</p>
        <h2>A missed deadline can cost an intake.</h2>
        <p className="tenure-product-lede">
          Start with the opening date and work backwards through school
          registration, planning, any foreign investment approval, construction
          and readiness to teach. These workstreams can overlap, but each has
          dependencies.
        </p>
        <div className="tenure-product-output-grid">
          <article>
            <h3>31 March</h3>
            <p>
              NESA applications for a new NSW non-government school are due by
              31 March in the year before opening.
            </p>
            <p className="tenure-product-cta">
              <a href="https://www.nsw.gov.au/education-and-training/nesa/school-regulation/non-government-schools/register-new-school">
                Source: NESA
              </a>
            </p>
          </article>
          <article>
            <h3>28 days</h3>
            <p>
              NSW State significant development applications generally require
              at least 28 days of public exhibition. That is one stage, not the
              full assessment or construction programme.
            </p>
            <p className="tenure-product-cta">
              <a href="https://www.planning.nsw.gov.au/the-planning-system/development-assessment/planning-approval-pathways/state-significant-development">
                Source: NSW Planning
              </a>
            </p>
          </article>
        </div>
        <p className="tenure-product-lede">
          For a new purpose-built campus, test a 2–3 year lead-time allowance at
          the outset and allow more for complex sites. This is an early planning
          assumption, not a national minimum or an approval-time guarantee.
        </p>
        <p className="tenure-product-helper">
          NSW examples, checked September 2026. Registration rules and planning
          pathways differ by state and project; foreign investment approval
          applies where required. Missing an application cycle may put the
          intended intake at risk.
        </p>
      </section>

      <section
        id="orbit-roadmap"
        className="tenure-product-section tenure-reveal"
      >
        <p className="tenure-product-eyebrow">
          Planned integration · ORBIT + CBSI
        </p>
        <h2>Taking a school across borders?</h2>
        <p className="tenure-product-lede">
          The planned ORBIT integration with CBSI would map education
          requirements alongside property, physical and legal standards across
          jurisdictions. The aim: surface differences in curriculum,
          registration, premises and evidence requirements before an
          international campus commits to a site.
        </p>
        <p className="tenure-product-lede">
          For example, NSW school registration and approval to teach students on
          Australian student visas are separate requirements. School providers
          serving those students also need the relevant NESA approval and CRICOS
          registration.
        </p>
        <p className="tenure-product-cta">
          <a href="https://www.nsw.gov.au/education-and-training/nesa/school-regulation/overseas-students">
            Source: NESA — overseas students
          </a>
        </p>
        <p className="tenure-product-helper">
          A roadmap capability, not currently available in TENURE. Cross-border
          mappings would support specialist review; they would not grant local
          approval or certify compliance.
        </p>
      </section>
    </>
  );
}
